import { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw, Volume2 } from "lucide-react";

const SRC = "/assets/poolside-theme.m4a";

function fmt(t) {
  if (!t || !isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export function ThemePlayer() {
  const audioRef = useRef(null);
  const barRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.85;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => setPlaying(false);
    const pause = () => {
      a.pause();
      setPlaying(false);
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    window.addEventListener("poolside:video-play", pause);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      window.removeEventListener("poolside:video-play", pause);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    const bar = barRef.current;
    if (!a || !bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    a.currentTime = ratio * duration;
    setTime(a.currentTime);
  };

  const replay = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play().catch(() => setPlaying(false));
    setPlaying(true);
  };

  const changeVolume = (e) => {
    const v = Number(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  const pct = duration ? (time / duration) * 100 : 0;

  return (
    <div
      className="relative overflow-hidden bg-white/70 backdrop-blur-xl border border-pool/20 rounded-3xl shadow-[0_20px_60px_rgba(2,60,80,0.12)] px-5 py-5 sm:px-7 sm:py-6"
      data-testid="theme-player"
    >
      <span
        aria-hidden="true"
        className={`theme-sweep pointer-events-none absolute -inset-y-8 w-1/3 bg-gradient-to-r from-transparent via-white/50 to-transparent -skew-x-12 ${playing ? "theme-sweep-on" : ""}`}
      />
      <audio ref={audioRef} src={SRC} preload="metadata" data-testid="theme-audio" />
      <div className="relative flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause the Poolside theme" : "Play the Poolside theme"}
          data-testid="theme-play-button"
          className="relative shrink-0 h-16 w-16 rounded-full bg-pool hover:bg-pool-dark text-white shadow-[0_14px_36px_rgba(2,150,180,0.35)] transition-all hover:scale-105 flex items-center justify-center"
        >
          {playing ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
          {playing && (
            <span aria-hidden="true" className="absolute inset-0 rounded-full border-2 border-pool/50 animate-ping [animation-duration:2.4s]" />
          )}
        </button>
        <div className="flex-1 w-full text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-3">
            <p className="font-display font-bold text-ink text-sm sm:text-base" data-testid="theme-title">
              Official Poolside Cooking Theme
            </p>
            <span className={`theme-eq flex items-end gap-[3px] h-4 ${playing ? "theme-eq-on" : ""}`} aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className="w-[3px] h-full rounded-full bg-pool/80" style={{ animationDelay: `${i * 0.18}s` }} />
              ))}
            </span>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-[11px] font-semibold text-slate-500 tabular-nums w-9 text-right" data-testid="theme-time">
              {fmt(time)}
            </span>
            <div
              ref={barRef}
              onClick={seek}
              className="relative flex-1 h-2 rounded-full bg-pool/10 cursor-pointer"
              data-testid="theme-progress"
              role="slider"
              aria-label="Seek through the theme song"
              aria-valuenow={Math.round(pct)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-pool to-pool-light" style={{ width: `${pct}%` }} />
              <span
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3.5 w-3.5 rounded-full bg-white border-2 border-pool shadow"
                style={{ left: `${pct}%` }}
              />
            </div>
            <span className="text-[11px] font-semibold text-slate-500 tabular-nums w-9" data-testid="theme-duration">
              {fmt(duration)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <button
            type="button"
            onClick={replay}
            aria-label="Replay the Poolside theme"
            data-testid="theme-replay"
            className="h-10 w-10 rounded-full bg-white/80 border border-pool/25 text-pool-deep hover:text-pool hover:border-pool flex items-center justify-center transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-pool-deep" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={changeVolume}
              aria-label="Theme song volume"
              data-testid="theme-volume"
              className="w-20 accent-[#06B6D4] cursor-pointer"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
